import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { LogOut, ShieldCheck } from "lucide-react";
import BackgroundOrbs from "../components/BackgroundOrbs";
import AdminErrorBoundary from "../components/admin/AdminErrorBoundary";
import QuestionsTab from "../components/admin/QuestionsTab";
import SessionsTab from "../components/admin/SessionsTab";
import SettingsTab from "../components/admin/SettingsTab";
import { adminSignOut, fetchAdminSession } from "../lib/api";
import { setAdminSessionExpiredHandler, isNcbAdmin } from "../lib/adminAuth";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [adminUser, setAdminUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("sessions");

  useEffect(() => {
    let cancelled = false;
    fetchAdminSession()
      .then((user) => {
        if (cancelled) return;
        if (!user) {
          navigate("/admin/login");
          return;
        }
        setAdminUser(user);
      })
      .catch(() => {
        if (!cancelled) navigate("/admin/login");
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    setAdminSessionExpiredHandler(() => {
      setAdminUser(null);
      navigate("/admin/login", { replace: true, state: { sessionExpired: true } });
    });
    return () => setAdminSessionExpiredHandler(null);
  }, [navigate]);

  const logout = async () => {
    try {
      await adminSignOut();
    } catch {
      /* ignore */
    }
    setAdminUser(null);
    navigate("/admin/login");
  };

  if (checking || !adminUser) return null;

  return (
    <AdminErrorBoundary>
    <div className="min-h-screen bg-md-navy relative overflow-hidden">
      <BackgroundOrbs />

      <header className="relative z-20 border-b border-md-cyan/10 bg-md-panel/30 backdrop-blur-lg sticky top-0">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-md-electric/15 border border-md-cyan/40 flex items-center justify-center">
              <ShieldCheck className="text-md-cyan" size={18} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-white/45">Salle de contrôle</div>
              <div className="text-lg font-bold text-md-silver">Money Drop Admin</div>
            </div>
          </div>
          <Button variant="ghost" onClick={logout} className="text-white/80 hover:text-md-cyan hover:bg-white/5" data-testid="admin-logout">
            <LogOut size={16} className="mr-2" /> Déconnexion
          </Button>
        </div>
      </header>

      <main className="relative z-10 container mx-auto px-6 py-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-md-panel/40 border border-md-cyan/15">
            <TabsTrigger value="sessions" data-testid="tab-sessions" className="data-[state=active]:bg-md-cyan/20 data-[state=active]:text-md-cyan">
              Sessions & vidéos
            </TabsTrigger>
            <TabsTrigger value="questions" data-testid="tab-questions" className="data-[state=active]:bg-md-cyan/20 data-[state=active]:text-md-cyan">
              Questions
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-md-cyan/20 data-[state=active]:text-md-cyan">
              Paramètres
            </TabsTrigger>
          </TabsList>

          <TabsContent value="sessions" className="mt-6">
            <SessionsTab isActive={tab === "sessions"} isAdminRole={isNcbAdmin(adminUser)} />
          </TabsContent>
          <TabsContent value="questions" className="mt-6">
            <QuestionsTab />
          </TabsContent>
          <TabsContent value="settings" className="mt-6">
            <SettingsTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
    </AdminErrorBoundary>
  );
};

export default AdminDashboard;
